<script setup lang="ts">
const auth = useAuthStore()
const { sidebarOpen, close, toggle } = useSidebar()
const route = useRoute()

/*
 * C'est le panneau qui défile en mobile, pas le menu qu'il contient : le
 * composable s'attache donc ici, sur le conteneur superposé.
 */
const { el: panneauEl, remember: retenirPanneau, restore: restaurerPanneau } = useSidebarScroll('mobile')


// Fermer la sidebar quand on navigue (mobile)
watch(() => route.path, () => {
  // Relever avant la fermeture : le panneau est encore monté et sa valeur juste.
  retenirPanneau()
  close()
})
</script>

<template>
  <div>
    <AppHeader />
    <main class="min-h-[calc(100vh-3.5rem)] bg-[var(--ui-bg)]">
      <UContainer class="py-6">
        <!-- Bouton hamburger mobile -->
        <div class="mb-4 lg:hidden">
          <UButton icon="i-lucide-menu" label="Menu" color="neutral" variant="outline" size="sm" @click="toggle" />
        </div>

        <div class="flex items-start gap-6">
          <!-- Sidebar desktop -->
          <div class="hidden lg:block">
            <AdminSidebar scrollable />
          </div>

          <!-- Sidebar mobile (overlay) -->
          <Teleport to="body">
            <!--
              La position est reposée depuis `@enter` plutôt que d'un `watch` +
              `nextTick` : le panneau naît d'un `v-if` à l'intérieur d'une
              transition, et son nœud n'est garanti présent qu'une fois ce hook
              appelé. Un `nextTick` gagnait la course en pratique, sans que rien
              ne l'assure.
            -->
            <Transition name="sidebar" @enter="restaurerPanneau">
              <div v-if="sidebarOpen" class="fixed inset-0 z-50 lg:hidden">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/40" @click="retenirPanneau(); close()" />
                <!-- Panneau -->
                <div ref="panneauEl" class="absolute inset-y-0 left-0 w-64 bg-[var(--ui-bg)] shadow-xl overflow-y-auto">
                  <div class="flex items-center justify-between px-4 py-3 border-b border-[var(--ui-border)]">
                    <span class="text-sm font-semibold text-[var(--ui-text)]">Menu</span>
                    <UButton icon="i-lucide-x" color="neutral" variant="ghost" size="sm" @click="retenirPanneau(); close()" />
                  </div>
                  <div class="p-2">
                    <AdminSidebar />
                  </div>
                </div>
              </div>
            </Transition>
          </Teleport>

          <!-- Contenu -->
          <div class="min-w-0 flex-1">
            <!--
              Dans la colonne du contenu, et non au-dessus des deux colonnes :
              le menu est fixé sous l'en-tête et recouvrait un bandeau placé
              en pleine largeur.
            -->
            <UAlert
              v-if="auth.profilNonVerifie"
              color="warning"
              variant="subtle"
              icon="i-lucide-shield-alert"
              title="Droits non vérifiés"
              description="Le service d'authentification n'a pas répondu : votre profil n'a pas pu être lu et vos droits sont réduits. Rechargez la page dans un instant."
              class="mb-4"
            />
            <!--
              Dit une fois, en tête de chaque écran : un lecteur voit les mêmes
              formulaires qu'un éditeur, et découvrirait sinon la consultation
              seule au moment d'enregistrer.
            -->
            <UAlert
              v-else-if="auth.authenticated && !auth.peutEcrire"
              color="info"
              variant="subtle"
              icon="i-lucide-eye"
              title="Consultation seule"
              description="Votre rôle permet de consulter les contenus, pas de les modifier. Pour obtenir le droit d'écrire, adressez-vous à un administrateur."
              class="mb-4"
            />
            <slot />
          </div>
        </div>
      </UContainer>
    </main>
  </div>
</template>

<style>
.sidebar-enter-active,
.sidebar-leave-active {
  transition: opacity 200ms ease;
}
.sidebar-enter-active > div:last-child,
.sidebar-leave-active > div:last-child {
  transition: transform 200ms ease;
}
.sidebar-enter-from,
.sidebar-leave-to {
  opacity: 0;
}
.sidebar-enter-from > div:last-child,
.sidebar-leave-to > div:last-child {
  transform: translateX(-100%);
}
</style>
